import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { helpService } from '@services';
import type { HelpCategory, HelpArticleWithCategory } from '@app-types';

export interface HelpCategoryGroup {
  category: HelpCategory;
  articles: HelpArticleWithCategory[];
}

export interface UseHelpPageDataReturn {
  groups: HelpCategoryGroup[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Data hook for the public Help page (/help): fetches categories and
 * articles in parallel and groups articles under their category, both
 * ordered by sort_order, so the page only has to render.
 */
export function useHelpPageData(): UseHelpPageDataReturn {
  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ['help-categories'],
    queryFn: () => helpService.getCategories()
  });

  const {
    data: articles,
    isLoading: articlesLoading,
    error: articlesError
  } = useQuery({
    queryKey: ['help-articles'],
    queryFn: () => helpService.getArticles()
  });

  const groups = useMemo<HelpCategoryGroup[]>(() => {
    if (!categories) return [];

    const sortedCategories = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    const articlesByCategory = new Map<number, HelpArticleWithCategory[]>();
    (articles ?? []).forEach((article) => {
      const list = articlesByCategory.get(article.category_id) ?? [];
      list.push(article);
      articlesByCategory.set(article.category_id, list);
    });

    return sortedCategories.map((category) => ({
      category,
      // Articles are already pre-sorted by sort_order by the backend; the
      // grouping above preserves that relative order.
      articles: articlesByCategory.get(category.id) ?? []
    }));
  }, [categories, articles]);

  return {
    groups,
    isLoading: categoriesLoading || articlesLoading,
    error: (categoriesError as Error | null) ?? (articlesError as Error | null)
  };
}
